import { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getDashboards, deleteDashboard, getDashboardTemplates, cloneDashboardFromTemplate,
  type Dashboard, type DashboardFilters,
} from '@/api/dashboardApi';
import { useAuth } from '@/hooks/useAuth';

export default function DashboardListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<DashboardFilters>({});
  const [templates, setTemplates] = useState<Dashboard[]>([]);

  const canWrite = hasPermission('dashboards', 'write');
  const canDelete = hasPermission('dashboards', 'delete');

  useEffect(() => {
    getDashboardTemplates()
      .then((res) => setTemplates(res.data?.items ?? []))
      .catch(() => {});
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof DashboardFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getDashboards({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('dashboards.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteDashboard(id);
            message.success(t('dashboards.delete_success'));
            gridRef.current?.reload();
          } catch (err) {
            message.error(err instanceof Error ? err.message : t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleCloneTemplate = useCallback(
    async (templateKey: string) => {
      try {
        await cloneDashboardFromTemplate(templateKey);
        message.success(t('dashboards.clone_success'));
        gridRef.current?.reload();
      } catch (err) {
        message.error(err instanceof Error ? err.message : t('app.error'));
      }
    },
    [t]
  );

  const columns: ColumnsType<Dashboard> = [
    {
      title: t('dashboards.field.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('dashboards.field.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val: string) => val || '—',
    },
    {
      title: t('dashboards.field.layout'),
      dataIndex: 'layout',
      key: 'layout',
      width: 100,
      render: (val: string) => <Tag>{val}</Tag>,
    },
    {
      title: t('dashboards.field.widgets'),
      dataIndex: 'widgets',
      key: 'widgets',
      width: 90,
      align: 'center',
      render: (val: unknown[]) => val?.length ?? 0,
    },
    {
      title: t('dashboards.field.owner'),
      dataIndex: 'owner_id',
      key: 'owner_id',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('dashboards.field.is_default'),
      dataIndex: 'is_default',
      key: 'is_default',
      width: 90,
      align: 'center',
      render: (val: boolean) => val ? <Tag color="green">{t('app.yes')}</Tag> : '—',
    },
    {
      title: t('dashboards.field.is_template'),
      dataIndex: 'is_template',
      key: 'is_template',
      width: 90,
      align: 'center',
      render: (val: boolean) => val ? <Tag color="blue">{t('dashboards.templates')}</Tag> : '—',
    },
    {
      title: t('app.actions'),
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_: unknown, record: Dashboard) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/dashboards/${record.id}`)}
          />
          {canWrite && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/dashboards/${record.id}/edit`)}
            />
          )}
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 160 }}
            placeholder={t('dashboards.field.is_template')}
            allowClear
            value={filters.is_template}
            onChange={(val) => handleFilterChange('is_template', val)}
            options={[
              { value: 'true', label: t('dashboards.templates') },
              { value: 'false', label: t('dashboards.title') },
            ]}
          />
        </Space>
        <Space>
          {canWrite && templates.length > 0 && (
            <Select
              style={{ width: 200 }}
              placeholder={t('dashboards.use_template')}
              options={templates.map((tpl) => ({
                value: tpl.template_key,
                label: tpl.name,
              }))}
              onChange={(val) => { if (val) handleCloneTemplate(val); }}
              value={undefined}
            />
          )}
          {canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/dashboards/new')}
            >
              {t('dashboards.new_dashboard')}
            </Button>
          )}
        </Space>
      </div>
      <DataGrid<Dashboard>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchPlaceholder={t('dashboards.search_placeholder')}
      />
    </div>
  );
}
